import { CheckCircle2, CircleSlash, FileText, Send, Stamp, XCircle } from 'lucide-react';
import { createElement } from 'react';
import type { LifecycleEvent } from '../../../../components/LifecycleTimeline';
import type { ApprovalRequestDetail } from '../../../approvals/approvalTypes';
import {
  APPROVAL_DECISION,
  APPROVAL_STATUS,
  APPROVAL_STEP_STATUS,
  approvalOutcomeIsRestated,
} from '../../../approvals/approvalTypes';
import { PR_STATUS } from '../purchaseRequestTypes';
import type { PurchaseRequestDetail } from '../purchaseRequestTypes';

/** 16px, which is what `<LifecycleTimeline>`'s rail is sized for. */
const glyph = (icon: typeof FileText) => createElement(icon, { className: 'h-4 w-4' });

/**
 * The request's lifecycle, DERIVED — from its own columns and from the approval the engine
 * already holds against it.
 *
 * **Nothing here is stored, and that is what makes the timeline honest.** The budget plan's
 * section refuses a lifecycle panel because it would be *"a second history this module would
 * have to write and keep in step with the audit trail"* — and this writes no history: every
 * instant below is a column that already existed, so there is no second trail to fall out of
 * step with the first. See `<LifecycleTimeline>`, where the reversal is argued.
 *
 * **The approval is READ rather than copied onto the request.** `PurchaseRequestDetail` carries
 * `approvalRequestId` and the engine's own `GET /api/approvals/{id}` carries the chain, so the
 * page fetches it — which is the standing rule about one module's DTO not growing a copy of
 * another module's record, and the reason this returns an empty-but-valid timeline when the
 * reader lacks `approvals.read`.
 *
 * **`!= null` throughout, never `!==`.** Every field here came off the wire, and the API omits
 * a null property entirely (`DefaultIgnoreCondition = WhenWritingNull`) — so it arrives as
 * `undefined` and a strict comparison answers the opposite of what it was written to say. That
 * defect shipped on this very module's quotation card.
 */
export function purchaseRequestLifecycle(
  request: PurchaseRequestDetail,
  approval: ApprovalRequestDetail | null,
): LifecycleEvent[] {
  const events: LifecycleEvent[] = [];

  /*
    RAISED — always, and it is the one event every request has. The word is the register's own
    rather than "Created": a purchase request is RAISED by somebody who wants something, which
    is what the module's own Create form and its `PR-` number are about.
  */
  events.push({
    key: 'raised',
    label: 'Raised',
    at: request.createdAt,
    icon: glyph(FileText),
    tone: 'slate',
    actor: request.createdByName,
  });

  if (approval != null) {
    /*
      SUBMITTED is the APPROVAL's own creation instant, not the request's `updatedAt`. The two
      acts are one transaction — `submitImmediately` writes the record and its approval
      together — so on a skip-draft request they are the same moment, and on a draft submitted
      later the approval's own row is the only thing that knows when.
    */
    events.push({
      key: 'submitted',
      label: 'Submitted for approval',
      at: approval.createdAt,
      icon: glyph(Send),
      tone: 'blue',
      actor: approval.requestedByName ?? request.createdByName,
    });

    /*
      ONE ROW PER SIGNATURE, and only for a step somebody has actually DECIDED. A waiting step
      has no instant, so a timeline row for it would have to invent one — and `<LifecycleTimeline>`'s
      own contract refuses that: a reader trusts a timeline about exactly the thing it would be
      guessing at. What is still outstanding is the banner's job to say, not this rail's.

      The LABEL is the step's own `statusLabel` — Approved, Rejected, Acknowledged — because the
      engine already has the word and inventing a second one for it is the failure the
      never-invent-terminology rule names.
    */
    for (const step of approval.steps) {
      if (step.decidedAt == null) continue;

      const rejected = step.status === APPROVAL_STEP_STATUS.rejected;
      const acknowledged = step.decisionType === APPROVAL_DECISION.acknowledge;

      events.push({
        key: `step-${step.id}`,
        label: step.statusLabel,
        at: step.decidedAt,
        icon: glyph(rejected ? XCircle : acknowledged ? Stamp : CheckCircle2),
        tone: rejected ? 'red' : acknowledged ? 'blue' : 'emerald',
        actor: step.approverName,
        detail: step.decisionReason,
      });
    }

    /*
      THE OUTCOME, and ONLY where no signature has already stated it.

      On an ordinary chain the last approver signing IS the chain completing, so drawing both put
      one fact in two rows a pixel apart — which is what a reader reported: two `Approved` rows,
      4:55 PM, one attributed and one not.

      **The test is the MEANING, never the instant.** This compared `completedAt` against each
      step's `decidedAt`, on the premise that the two are the same moment. They never are:
      `ApprovalService` reads the clock once for the step and again for the request, so they
      differ by a few hundred microseconds and the guard could not fire. See
      `approvalOutcomeIsRestated`, which both builders now share.
    */
    if (approval.completedAt != null && !approvalOutcomeIsRestated(approval)) {
      const rejected = approval.status === APPROVAL_STATUS.rejected;

      events.push({
        key: 'outcome',
        label: approval.statusLabel,
        at: approval.completedAt,
        icon: glyph(rejected ? XCircle : CheckCircle2),
        tone: rejected ? 'red' : 'emerald',
        actor: null,
        detail: approval.outcomeReason,
      });
    }
  }

  /*
    CANCELLED — the request's own act, and the one transition that does not go through the
    engine: `CancelForSubjectAsync` closes the approval and fires no callback, because the
    subject is what called it.

    The instant is `updatedAt`, which is the closest honest answer the record holds: cancelling
    is the last thing that happened to a cancelled request, so its own update stamp IS that
    moment. It falls back to the creation only so the row cannot carry an empty instant.
  */
  if (request.status === PR_STATUS.cancelled) {
    events.push({
      key: 'cancelled',
      label: 'Cancelled',
      at: request.updatedAt ?? request.createdAt,
      icon: glyph(CircleSlash),
      tone: 'slate',
      actor: request.updatedByName,
      detail: request.cancellationReason,
    });
  }

  return events;
}
